import {
  looseUnits,
  palletLoad,
  type ConsignmentLineItem,
  type LineTotals,
  type PalletConfig,
  type PalletSummary
} from '@/entities/consignments';

import type { LocalPallet, PalletLineGroup } from '../model/types';
import { linePart } from './line-share';

/**
 * Builds the pallets from the pallet configuration (contents) and the lines
 * endpoint `pallets` array (type description and capacity, matched by UUID),
 * with the load of their units (`palletLoad`).
 */
export function buildPallets(
  config: PalletConfig,
  summaries: PalletSummary[] | undefined,
  lines: ConsignmentLineItem[]
): LocalPallet[] {
  const lineById = new Map(lines.map((line) => [line.id, line]));
  const summaryByUuid = new Map((summaries ?? []).map((summary) => [summary.uuid, summary]));

  return config.map((unit) => {
    const summary = summaryByUuid.get(unit.uuid);
    const units = Object.fromEntries(Object.entries(unit.items).filter(([lineId]) => lineById.has(lineId)));
    const { weight, volume } = palletLoad(units, lineById);

    return {
      id: unit.uuid,
      palletNumber: unit.uuid.slice(0, 8),
      typeId: unit.typeId,
      type: summary?.type ?? null,
      maxWeight: summary?.maxWeight ?? null,
      maxVolume: summary?.maxVolume ?? null,
      currentWeight: weight,
      currentVolume: volume,
      units
    };
  });
}

/**
 * Splits the lines into their loose part and their part on each pallet, both
 * ordered by line index; pallets keep their sequence order, empty ones included.
 * A line fully on pallets is not among the unassigned lines, like the legacy
 * widget hides it; a line without inventory units can never be palletized and
 * always is.
 */
export function groupLinesByPallet(
  lines: ConsignmentLineItem[],
  pallets: LocalPallet[],
  config: PalletConfig,
  totals: LineTotals
): { unassignedLines: ConsignmentLineItem[]; palletGroups: PalletLineGroup[] } {
  const byIndex = [...lines].sort((a, b) => a.index - b.index);

  return {
    unassignedLines: byIndex.flatMap((line) => {
      const total = totals[line.id] ?? 0;
      const loose = looseUnits(config, line.id, total);
      return total <= 0 || loose > 0 ? [linePart(line, loose)] : [];
    }),
    palletGroups: pallets.map((pallet) => ({
      pallet,
      lines: byIndex.filter((line) => pallet.units[line.id]).map((line) => linePart(line, pallet.units[line.id]))
    }))
  };
}
